package com.flowyride.core.map

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.awaitTouchSlopOrCancellation
import androidx.compose.foundation.gestures.drag
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import com.flowyride.core.designsystem.theme.FlowyColors
import com.flowyride.core.domain.model.GeoPoint
import org.maplibre.compose.map.MapState
import org.maplibre.compose.overlay.MapOverlayScope
import org.maplibre.spatialk.geojson.Position

/** How close to the line a press must be to grab it. */
private val GrabDistance = 16.dp

/** Presses this close to a marker belong to the marker. */
private val MarkerExclusion = 24.dp

/**
 * Lets the rider grab the route line and drop it elsewhere, like on web maps.
 * Presses away from the line are left untouched, so the map still pans.
 * A plain tap on the line is not consumed either: it stays a map tap.
 */
@Composable
internal fun MapOverlayScope.TrackDragLayer(
    track: List<GeoPoint>,
    markers: List<GeoPoint>,
    mapState: MapState,
    onDropped: (trackIndex: Int, GeoPoint) -> Unit,
) {
    val currentTrack by rememberUpdatedState(track)
    val currentMarkers by rememberUpdatedState(markers)
    val currentOnDropped by rememberUpdatedState(onDropped)
    var ghost by remember { mutableStateOf<Position?>(null) }

    Box(
        Modifier
            .matchParentSize()
            .pointerInput(mapState) {
                awaitEachGesture {
                    val down = awaitFirstDown(requireUnconsumed = false)
                    val grabbed = hitTrack(down.position, currentTrack, currentMarkers, mapState) ?: return@awaitEachGesture
                    val slop = awaitTouchSlopOrCancellation(down.id) { change, _ -> change.consume() } ?: return@awaitEachGesture
                    var pointer = slop.position
                    ghost = mapState.positionAt(pointer, this)
                    drag(slop.id) { change ->
                        change.consume()
                        pointer = change.position
                        mapState.positionAt(pointer, this)?.let { ghost = it }
                    }
                    ghost?.let { currentOnDropped(grabbed, GeoPoint(it.latitude, normalizeLongitude(it.longitude))) }
                    ghost = null
                }
            },
    )

    ghost?.let { position ->
        Box(
            Modifier
                .placedAt(position)
                .size(26.dp)
                .shadow(4.dp, CircleShape)
                .background(FlowyColors.Stop, CircleShape)
                .border(3.dp, Color.White, CircleShape),
        )
    }
}

/** @return the index of the track point starting the segment under [press], or null if the press misses the line. */
private fun Density.hitTrack(press: Offset, track: List<GeoPoint>, markers: List<GeoPoint>, mapState: MapState): Int? {
    if (track.size < 2) return null
    val exclusion = MarkerExclusion.toPx()
    val onMarker = markers.any { marker ->
        mapState.screenLocationFromPosition(marker.toPosition())?.let { (it.toPx(this) - press).getDistance() < exclusion } == true
    }
    if (onMarker) return null

    val grab = GrabDistance.toPx()
    var best: Int? = null
    var bestDistance = Float.MAX_VALUE
    var previous: Offset? = null
    track.forEachIndexed { index, point ->
        val current = mapState.screenLocationFromPosition(point.toPosition())?.toPx(this)
        if (current != null && previous != null) {
            val distance = distanceToSegment(press, previous!!, current)
            if (distance < bestDistance) {
                bestDistance = distance
                best = index - 1
            }
        }
        previous = current
    }
    return best.takeIf { bestDistance <= grab }
}

private fun MapState.positionAt(pointer: Offset, density: Density): Position? =
    with(density) { positionFromScreenLocation(DpOffset(pointer.x.toDp(), pointer.y.toDp())) }

private fun DpOffset.toPx(density: Density) = with(density) { Offset(x.toPx(), y.toPx()) }

/** Distance from [p] to the segment [a]–[b], in pixels. */
internal fun distanceToSegment(p: Offset, a: Offset, b: Offset): Float {
    val ab = b - a
    val lengthSquared = ab.x * ab.x + ab.y * ab.y
    if (lengthSquared == 0f) return (p - a).getDistance()
    val t = (((p - a).x * ab.x + (p - a).y * ab.y) / lengthSquared).coerceIn(0f, 1f)
    return (p - (a + ab * t)).getDistance()
}
